import { withTranslation } from 'react-i18next';

import Loading from '../../../../components/Loading';
import MetricCards from '../../GscTools/components/MetricCards';
import PeriodTrend from '../../GscTools/components/PeriodTrend';
import { comparability, metricsFor } from '../../GscTools/metrics';
import { formatMetric } from '../../GscTools/format';
import { SEGMENTS, SEGMENT_COLORS, segmentLabel } from '../brand';

/**
 * Block 03 — the two sides of the rule as daily lines, over the same days.
 *
 * Two query-filtered series, each fetched with the filter its own side is defined by. Non-brand
 * is never drawn by subtracting brand from the property's total: the difference between those two
 * is brand plus everything Search Console withheld, so a line drawn that way would rise and fall
 * with Google's disclosure thresholds and be read as demand.
 *
 * The cards above are the known-query totals — the two sides added together, which is this
 * report's denominator and the only total these two series may be added into — with each card's
 * note carrying the split that made it. Picking a card changes which metric the lines draw, which
 * is a change of presentation: both series are already here, in every metric, and nothing is
 * refetched.
 *
 * The totals are summed from the days actually returned rather than taken from block 02's
 * aggregates, so the cards and the lines under them are the same figures. They will not always
 * match block 02 exactly, and that is the honest outcome: an aggregate for a window is not the
 * sum of the days Search Console chose to return within it.
 */
function SegmentTrend({ t, scope, sides, known, metric, onMetric }) {
    const loading = SEGMENTS.some((segment) => {
        const status = sides[segment]?.status;
        return status === 'idle' || status === 'loading';
    });
    const failed = SEGMENTS.filter((segment) => sides[segment]?.status === 'error');
    const drawn = SEGMENTS.filter((segment) => sides[segment]?.status === 'done');

    const comparison = comparability(known.current?.coverage, known.baseline?.coverage ?? null);

    /** Each card's note: the split behind the known-query total it is showing. */
    const splitNote = (name) =>
        drawn
            .map(
                (segment) =>
                    `${segmentLabel(t, segment)} ${formatMetric(
                        name,
                        sides[segment]?.current?.totals?.[name],
                    )}`,
            )
            .join(' · ');

    const series = drawn.map((segment) => ({
        key: segment,
        label: segmentLabel(t, segment),
        baselineLabel: t('tools.gsc.trend.baseline_of', { label: segmentLabel(t, segment) }),
        color: SEGMENT_COLORS[segment],
        current: sides[segment]?.current,
        baseline: sides[segment]?.baseline,
    }));

    return (
        <div className="card table-main rounded-main overflow-hidden gsc-tool-block">
            <div className="card-header d-flex justify-content-between">
                <div>
                    <p className="card-title">{t('tools.brand_non_brand.trend.title')}</p>
                    <p className="gsc-tool-sub">{t('tools.brand_non_brand.trend.description')}</p>
                </div>
            </div>

            <div className="card-body">
                {loading && <Loading padding={20} />}

                {!loading &&
                    failed.map((segment) => (
                        <div key={segment} className="gsc-tool-banner error">
                            {t('tools.brand_non_brand.trend.side_failed', {
                                segment: segmentLabel(t, segment),
                                reason: sides[segment]?.error ?? t('global.something_went_wrong'),
                            })}
                        </div>
                    ))}

                {!loading && !!series.length && (
                    <>
                        <MetricCards
                            metrics={metricsFor(scope.searchType)}
                            totals={known.current?.totals}
                            priorTotals={known.baseline?.totals}
                            comparison={comparison}
                            selectedMetric={metric}
                            onSelectMetric={onMetric}
                            labelFor={(name) =>
                                t('tools.brand_non_brand.trend.known_metric', {
                                    // The short names, so the card reads "Known-query Clicks"
                                    // rather than "Known-query Total clicks". The prefix is not
                                    // decoration: a card labelled "Total clicks" here would be
                                    // read as the property's total, which is the one thing these
                                    // figures are not.
                                    metric: t(`tools.gsc.metrics.${name}_short`),
                                })
                            }
                            noteFor={(name, defaultNote) => splitNote(name) || defaultNote}
                        />

                        <p className="gsc-tool-hint">
                            {t('tools.brand_non_brand.trend.pick_card')}
                        </p>

                        <PeriodTrend
                            metric={metric}
                            series={series}
                            fileName={t('tools.brand_non_brand.trend.title')}
                        />

                        <p className="gsc-tool-table-note">
                            {t('tools.brand_non_brand.trend.no_subtraction')}
                        </p>
                        <p className="gsc-tool-table-note">
                            {t('tools.brand_non_brand.trend.gaps_note')}
                        </p>
                    </>
                )}
            </div>
        </div>
    );
}

export default withTranslation()(SegmentTrend);
